import { getApiUrl } from '@/lib/api-config';
import { trackInvoicePaymentReceived, trackFormSubmission } from '@/lib/posthog-events';
import { uploadInvoicePaymentAttachments } from './invoicePaymentAttachments';

/**
 * Creating an invoice payment, in ONE place: the Payments table's "New" and the invoice's "Create Payment" both go through here, so the request, the files that
 * wait for the new payment and the tracking cannot differ. `paymentData.client_request_id` (one per opening of the form) makes the same request sent twice save once.
 */
export async function postInvoicePayment(paymentData: Record<string, unknown>, who: { companyId: string; userId: string }): Promise<any> {
  const response = await fetch(getApiUrl('api/entities/invoice_payment'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: who.userId, company_id: who.companyId, ...paymentData, created_by: who.userId }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create payment');
  }
  return (await response.json()).data;
}

export interface NewPaymentFile { file: File; name: string; size: number; type: string }

/** Creates the payment, then uploads the files that were waiting for its id (a failed upload is logged: the payment itself is saved), and tracks the outcome. */
export async function createInvoicePayment(paymentData: Record<string, any>, newAttachments: NewPaymentFile[], who: { companyId: string; userId: string }): Promise<any> {
  try {
    const saved = await postInvoicePayment(paymentData, who);
    if (newAttachments.length > 0 && saved?.id) {
      try {
        await uploadInvoicePaymentAttachments(saved.id, newAttachments.map((a) => a.file), who.companyId, who.userId);
      } catch (uploadError) {
        console.error('Error uploading attachments after create:', uploadError);
      }
    }
    trackInvoicePaymentReceived({
      payment_id: saved.id, payment_number: paymentData.payment_number, project_id: paymentData.project_id, invoice_id: paymentData.invoice_id,
      amount: paymentData.amount, status: paymentData.status, payment_date: paymentData.payment_date, attachment_count: newAttachments.length,
      company_id: who.companyId, created_by: who.userId,
    });
    trackFormSubmission({ form_type: 'invoice_payment_creation', success: true });
    return saved;
  } catch (error: any) {
    console.error('Error saving payment:', error);
    trackFormSubmission({ form_type: 'invoice_payment_creation', success: false, error_message: error.message || 'Failed to save payment' });
    throw error;
  }
}
